import React, { useEffect, memo, useCallback, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { HvEmptyState } from '@hitachivantara/uikit-react-core';
import { Icon } from '@iq/react-components';
import { format, parseISO } from 'date-fns';

import {
  fetchAlarmLogbook,
  getLogbookByAlarmId,
  getLogbookLoading,
} from '../../../../../bundles/alarms';
import { getTimezone } from '../../../../../bundles/application';
import { fetchComments, getCommentsByParentId } from '../../../../../bundles/comments';
import { requestUser } from '../../../../../bundles/ad';
import Loader from '../../../../Loader';

import './LogbookTab.scss';
import { formatDate, UserAvatar, UserSignature } from '../../utils';

const LogbookRow = memo(({ alarmItem, isSelected, timezone, onClick }) => {
  const alarmComments = useSelector((state) => getCommentsByParentId(state, alarmItem.id)) || [];

  return (
    <div
      className={`logbook-row ${isSelected ? 'logbook-row--selected' : ''}`}
      onClick={() => onClick(alarmItem)}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          onClick(alarmItem);
        }
      }}
    >
      <span className="logbook-col logbook-col--date">
        {formatDate(alarmItem.resolvedAt, timezone)}
      </span>
      <span className="logbook-col logbook-col--notes">{alarmItem.resolvedText || '-'}</span>
      <span className="logbook-col logbook-col--comments">
        <Icon
          icon="he-comment"
          size="xs"
        />
        {alarmComments.length > 0 && `${alarmComments.length}`}
      </span>
    </div>
  );
});

const LogbookTab = memo(({ alarm }) => {
  const dispatch = useDispatch();
  const timezone = useSelector(getTimezone);
  const alarmId = alarm?.id;
  const logbookData = useSelector((state) => getLogbookByAlarmId(state, alarmId));
  const isLoading = useSelector((state) => getLogbookLoading(state, alarmId));
  const [selectedAlarm, setSelectedAlarm] = useState(null);
  const [isClosing, setIsClosing] = useState(false);
  const comments = useSelector((state) => getCommentsByParentId(state, selectedAlarm?.id)) || [];
  const alarms = logbookData?.alarms || [];
  const fetchedAlarmsRef = useRef(new Set());

  useEffect(() => {
    if (alarmId && !logbookData) {
      dispatch(fetchAlarmLogbook(alarm));
    }
  }, [alarmId, logbookData, dispatch, alarm]);

  useEffect(() => {
    if (alarms.length > 0) {
      alarms.forEach((alarmItem) => {
        if (!fetchedAlarmsRef.current.has(alarmItem.id)) {
          fetchedAlarmsRef.current.add(alarmItem.id);
          dispatch(fetchComments({ parentId: alarmItem.id }));
        }
      });
    }
  }, [alarms, dispatch]);

  useEffect(() => {
    if (selectedAlarm?.id) {
      dispatch(fetchComments({ parentId: selectedAlarm.id }));
    }
  }, [selectedAlarm?.id, dispatch]);

  useEffect(() => {
    if (comments.length > 0) {
      const userIds = [...new Set(comments.map((c) => c.createdBy))];
      userIds.forEach((userId) => {
        dispatch(requestUser(userId));
      });
    }
  }, [dispatch, comments]);

  useEffect(() => {
    if (alarms.length > 0) {
      const userIds = [...new Set(alarms.map((a) => a.resolvedBy).filter(Boolean))];
      userIds.forEach((userId) => {
        dispatch(requestUser(userId));
      });
    }
  }, [dispatch, alarms]);

  const handleRowClick = useCallback((alarmData) => {
    setSelectedAlarm((prev) => {
      if (prev?.id === alarmData.id) {
        setIsClosing(true);
        setTimeout(() => {
          setSelectedAlarm(null);
          setIsClosing(false);
        }, 300);
        return prev;
      }
      // Opening or switching
      setIsClosing(false);
      return alarmData;
    });
  }, []);

  if (isLoading) {
    return <Loader />;
  }

  if (logbookData && alarms.length === 0) {
    return (
      <div className="logbook-tab logbook-tab--empty">
        <HvEmptyState
          title="No previous alarms found"
          message="No resolved alarms of the same type were found in the logbook."
          icon={
            <Icon
              icon="he-info"
              size="l"
            />
          }
        />
      </div>
    );
  }

  if (!logbookData) {
    return <Loader />;
  }

  return (
    <div className="logbook-tab">
      <div className="logbook-header">
        <span>The log of previous resolved alarms that may help you fix this alarm</span>
      </div>
      <div className="logbook-container">
        <div className="logbook-list">
          <div className="logbook-list-header">
            <span className="logbook-col logbook-col--date">Resolved at</span>
            <span className="logbook-col logbook-col--notes">Resolution notes</span>
            <span className="logbook-col logbook-col--comments">Comments</span>
          </div>
          <div className="logbook-list-body">
            {alarms.map((alarmItem) => {
              const isSelected = selectedAlarm?.id === alarmItem.id;
              return (
                <LogbookRow
                  key={alarmItem.id}
                  alarmItem={alarmItem}
                  isSelected={isSelected}
                  timezone={timezone}
                  onClick={handleRowClick}
                />
              );
            })}
          </div>
        </div>

        {selectedAlarm && (
          <div className={`logbook-detail ${isClosing ? 'logbook-detail--closing' : ''}`}>
            <div className="logbook-detail-resolution">
              <div className="logbook-detail-resolved">
                <span className="logbook-detail-date">
                  {formatDate(selectedAlarm.resolvedAt, timezone)}
                </span>
                <span className="logbook-detail-user">
                  <span>Resolved by</span> <UserSignature userId={selectedAlarm.resolvedBy} />
                </span>
              </div>

              <div className="logbook-detail-notes">
                <ReadMoreText
                  text={selectedAlarm.resolvedText}
                  maxLength={150}
                />
              </div>
            </div>

            <div className="logbook-detail-comments">
              <h4>Comments</h4>
              <div className="comments-list">
                {comments.length === 0 ? (
                  <p className="no-comments">No comments yet</p>
                ) : (
                  comments.map((comment) => (
                    <CommentItem
                      key={comment.id}
                      comment={comment}
                    />
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

const CommentItem = memo(({ comment }) => {
  return (
    <div className="logbook-comment">
      <div className="logbook-comment__badge">
        <UserAvatar userId={comment.createdBy} />
      </div>
      <div className="logbook-comment__content">
        <div className="logbook-comment__byline">
          <div className="logbook-comment__created-by">
            <UserSignature userId={comment.createdBy} />
          </div>
          <div className="logbook-comment__created-at">
            {format(parseISO(comment.createdAt), 'd MMM')}
          </div>
        </div>
        <div className="logbook-comment__body">{comment.body}</div>
      </div>
    </div>
  );
});

const ReadMoreText = ({ text, maxLength = 200 }) => {
  const [expanded, setExpanded] = useState(false);

  if (!text) return <span className="no-content">-</span>;

  const needsTruncation = text.length > maxLength;
  const displayText = expanded || !needsTruncation ? text : `${text.slice(0, maxLength)}...`;

  return (
    <div className="read-more-text">
      <p>{displayText}</p>
      {needsTruncation && (
        <button
          type="button"
          className="read-more-button"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? 'Show less' : 'Read more...'}
        </button>
      )}
    </div>
  );
};

LogbookTab.displayName = 'LogbookTab';

export default LogbookTab;
